import {
  LayoutDashboard,
  TestTube,
  Beaker,
  FlaskConical,
  Receipt,
  Users,
  FileText,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  Plus,
  QrCode,
  Stethoscope,
  ShieldAlert
} from "lucide-react";
import { Button } from "./ui/button";
import { cn } from "./ui/utils";
import { useAuth } from "../contexts/AuthContext";
import { UserRole } from "../types/types";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Avatar, AvatarFallback } from "./ui/avatar";
import { useState } from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";

interface SidebarProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
}

const navigation: { id: string; icon: React.ComponentType<{ className?: string }>; label: string; roles?: string[] }[] = [
  { id: "dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { id: "blood-reports", icon: TestTube, label: "Blood Reports" },
  { id: "urine-reports", icon: Beaker, label: "Urine Reports" },
  { id: "stool-reports", icon: FlaskConical, label: "Stool Reports" },
  { id: "billing", icon: Receipt, label: "Billing" },
  { id: "patients", icon: Users, label: "Patients" },
  { id: "reports-history", icon: FileText, label: "Reports History" },
  { id: "barcode-generator", icon: QrCode, label: "Barcode Generator" },
  { id: "doctors", icon: Stethoscope, label: "Doctors" },
  { id: "audit-logs", icon: ShieldAlert, label: "Audit Logs", roles: ['owner', 'admin', 'super_admin'] },
];

export function Sidebar({ activeTab, onTabChange }: SidebarProps) {
  const { user, logout, currentLab, userProfile } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState(false);

  const userRole: UserRole = userProfile?.role || 'viewer';

  const filteredNavigation = navigation.filter(item => {
    if (!item.roles) return true;
    return item.roles.includes(userRole);
  });

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const getUserInitials = (email: string) => {
    return email.split('@')[0].slice(0, 2).toUpperCase();
  };

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  return (
    <TooltipProvider>
      <div className={cn(
        "bg-sidebar text-sidebar-foreground border-r border-sidebar-border flex flex-col transition-all duration-300",
        isCollapsed ? "w-16" : "w-64"
      )}>
        {/* Header */}
        <div className={cn(
          "p-4 border-b border-sidebar-border",
          isCollapsed ? "p-2" : "p-6"
        )}>
          {isCollapsed ? (
            // Collapsed header - centered layout
            <div className="flex flex-col items-center space-y-3">
              <div className="w-10 h-10 shrink-0 rounded-full bg-sidebar-primary text-sidebar-primary-foreground flex items-center justify-center font-bold text-sm">
                CL
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleSidebar}
                className="h-6 w-6 p-0 text-sidebar-foreground hover:bg-sidebar-accent"
              >
                <ChevronRight className="h-3 w-3" />
              </Button>
            </div>
          ) : (
            // Expanded header - full layout
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 shrink-0 rounded-full bg-sidebar-primary text-sidebar-primary-foreground flex items-center justify-center font-bold">
                  GC
                </div>
                <div>
                  <h1 className="text-lg font-semibold">GCLabs</h1>
                  <p className="text-sm text-sidebar-foreground/60">Laboratory Management</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleSidebar}
                className="h-8 w-8 p-0 text-sidebar-foreground hover:bg-sidebar-accent"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2">
          {filteredNavigation.map((item) => {
            const Icon = item.icon;
            const navButton = (
              <Button
                key={item.id}
                variant="ghost"
                className={cn(
                  "w-full text-left transition-all",
                  isCollapsed ? "justify-center px-2" : "justify-start",
                  activeTab === item.id
                    ? "bg-zinc-900 text-white dark:bg-white dark:text-zinc-900 shadow-sm rounded-md px-3 py-2 hover:bg-zinc-800 dark:hover:bg-zinc-100"
                    : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:rounded-md hover:px-3 hover:py-2"
                )}
                onClick={() => onTabChange(item.id)}
              >
                <Icon className={cn("h-4 w-4", !isCollapsed && "mr-3")} />
                {!isCollapsed && item.label}
              </Button>
            );

            if (isCollapsed) {
              return (
                <Tooltip key={item.id}>
                  <TooltipTrigger asChild>
                    {navButton}
                  </TooltipTrigger>
                  <TooltipContent side="right" className="ml-2">
                    {item.label}
                  </TooltipContent>
                </Tooltip>
              );
            }

            return navButton;
          })}
        </nav>

        {/* Create New Test Button */}
        <div className={cn(
          "px-4 pb-2",
          isCollapsed && "px-2"
        )}>
          <Button
            variant="default"
            className={cn(
              "w-full transition-all",
              isCollapsed ? "px-0 justify-center" : "justify-start"
            )}
            onClick={() => onTabChange("create-test")}
          >
            <Plus className={cn("h-4 w-4", !isCollapsed && "mr-2")} />
            {!isCollapsed && "Create New Test"}
          </Button>
        </div>

        {/* User Menu */}
        <div className={cn(
          "p-4 border-t border-sidebar-border",
          isCollapsed && "p-2"
        )}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className={cn(
                  "w-full h-auto transition-all",
                  isCollapsed ? "p-2 justify-center" : "p-2 justify-start"
                )}
              >
                {isCollapsed ? (
                  // Collapsed user menu - just avatar
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="text-xs bg-sidebar-primary text-sidebar-primary-foreground">
                      {user?.email ? getUserInitials(user.email) : "AD"}
                    </AvatarFallback>
                  </Avatar>
                ) : (
                  // Expanded user menu - full layout
                  <div className="flex items-center space-x-3 w-full">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="text-xs bg-sidebar-primary text-sidebar-primary-foreground">
                        {user?.email ? getUserInitials(user.email) : "AD"}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 text-left">
                      <p className="text-sm font-medium">{currentLab?.name || "Lab Admin"}</p>
                      <p className="text-xs text-sidebar-foreground/60 truncate">
                        {user?.email || "admin@gc-pathological.com"}
                      </p>
                    </div>
                  </div>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-56"
              align={isCollapsed ? "center" : "end"}
              side={isCollapsed ? "right" : "top"}
            >
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium leading-none">{currentLab?.name || "Lab Admin"}</p>
                  <p className="text-xs leading-none text-muted-foreground">
                    {user?.email || "admin@gc-pathological.com"}
                  </p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                <DropdownMenuItem onClick={() => onTabChange("settings")}>
                  <Settings className="mr-2 h-4 w-4" />
                  Settings
                </DropdownMenuItem>

                <DropdownMenuItem onClick={() => onTabChange("subscription")}>
                  <CreditCard className="mr-2 h-4 w-4" />
                  Subscription
                </DropdownMenuItem>
                {(userRole === 'owner' || userRole === 'admin') && (
                  <DropdownMenuItem onClick={() => onTabChange("team")}>
                    <Users className="mr-2 h-4 w-4" />
                    Team
                  </DropdownMenuItem>
                )}
              </DropdownMenuGroup>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600 focus:text-red-600">
                <LogOut className="mr-2 h-4 w-4" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </TooltipProvider>
  );
}